import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { toServiceArray } from '@/utils/seoUtils';
import { toast } from 'sonner';

const FavoritesContext = createContext();

export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    const checkUser = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        }
      } catch (e) {
        console.log('Not logged in');
      }
    };
    checkUser();
  }, []);

  // Load favorites from backend or localStorage
  useEffect(() => {
    const loadFavorites = async () => {
      setLoading(true);
      try {
        if (user) {
          // Load from backend for logged-in users
          const res = await fetch(`${API}/favorites`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setFavorites(data.favorites || []);
          }
        } else {
          // Load from localStorage for guests
          const stored = localStorage.getItem('vroum_favorites');
          if (stored) {
            setFavorites(JSON.parse(stored));
          }
        }
      } catch (e) {
        console.error('Error loading favorites:', e);
      } finally {
        setLoading(false);
      }
    };
    loadFavorites();
  }, [user]);

  // Save to localStorage for guests
  useEffect(() => {
    if (!user && favorites.length >= 0) {
      localStorage.setItem('vroum_favorites', JSON.stringify(favorites));
    }
  }, [favorites, user]);

  const addToFavorites = useCallback(async (garage) => {
    if (favorites.find(g => g.garage_id === garage.garage_id)) {
      return false;
    }

    const newFavorite = {
      garage_id: garage.garage_id,
      name: garage.name,
      slug: garage.slug,
      city: garage.city,
      rating: garage.rating,
      phone: garage.phone,
      services: toServiceArray(garage.services).slice(0, 3),
      added_at: new Date().toISOString()
    };

    // Optimistic update
    setFavorites(prev => [...prev, newFavorite]);

    if (user) {
      try {
        const res = await fetch(`${API}/favorites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ garage_id: garage.garage_id })
        });
        if (!res.ok) {
          throw new Error('Failed to save favorite');
        }
      } catch (e) {
        // Rollback on error
        setFavorites(prev => prev.filter(g => g.garage_id !== garage.garage_id));
        toast.error('Σφάλμα κατά την αποθήκευση');
        return false;
      }
    }

    toast.success(`${garage.name} προστέθηκε στα αγαπημένα`);
    return true;
  }, [favorites, user]);

  const removeFromFavorites = useCallback(async (garageId) => {
    const garage = favorites.find(g => g.garage_id === garageId);
    
    // Optimistic update
    setFavorites(prev => prev.filter(g => g.garage_id !== garageId));

    if (user) {
      try {
        const res = await fetch(`${API}/favorites/${garageId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (!res.ok) {
          throw new Error('Failed to remove favorite');
        }
      } catch (e) {
        // Rollback on error
        if (garage) {
          setFavorites(prev => [...prev, garage]);
        }
        toast.error('Σφάλμα κατά την αφαίρεση');
        return false;
      }
    }

    toast.info('Αφαιρέθηκε από τα αγαπημένα');
    return true;
  }, [favorites, user]);

  const isFavorite = useCallback((garageId) => {
    return favorites.some(g => g.garage_id === garageId);
  }, [favorites]);

  const toggleFavorite = useCallback(async (garage) => {
    if (isFavorite(garage.garage_id)) {
      return removeFromFavorites(garage.garage_id);
    } else {
      return addToFavorites(garage);
    }
  }, [isFavorite, addToFavorites, removeFromFavorites]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      loading,
      user,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      toggleFavorite,
      favoritesCount: favorites.length
    }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
